import { t } from 'i18next';

type Bar = readonly [x: number, y: number, width: number];

// Drawn on a 592 wide page whose top left corner is the origin.
const INVOICE_HEADINGS: readonly Bar[] = [
  [42, 260, 64],
  [165, 260, 63],
  [274, 260, 63],
  [383, 260, 63],
  [492, 260, 63],
  [45, 514, 63],
  [167, 514, 64],
];

const INVOICE_LINES: readonly Bar[] = [
  [42, 81, 104],
  [42, 94.5, 96],
  [42, 108.5, 96],
  [459, 81, 96],
  [459, 94.5, 96],
  [42, 179, 104],
  [42, 193, 96],
  [42, 206.5, 96],
  [258, 179, 104],
  [258, 193, 95],
  [258, 206.5, 95],
  ...[288, 307.5, 326.5, 345.5].flatMap((y): Bar[] => [
    [42, y, 104],
    [165, y, 39],
    [274, y, 38],
    [383, y, 38],
    [492, y, 38],
  ]),
  [421, 411, 79],
  [519, 411, 36],
  [459, 427, 41],
  [519, 427, 36],
  [446, 443.5, 54],
  [519, 443.5, 36],
  [432, 457, 68],
  [519, 457, 36],
  [45, 533.5, 104],
  [168, 533.5, 38],
];

const CONTENT_ZONES = [
  { top: 646.5, stroke: 'stroke-illustration-primary', text: 'fill-illustration-primary-text' },
  { top: 744.5, stroke: 'stroke-amber-500', text: 'fill-amber-500' },
  { top: 843.5, stroke: 'stroke-emerald-600', text: 'fill-emerald-600' },
] as const;

const EMAIL_PANELS = [203, 485] as const;

/**
 * The body of an invoice: the header, the addresses, the line items, the totals and the tax
 * summary. It has no page edge, so each picture frames it at its own size with `transform`.
 */
export function InvoiceDocument({ transform }: { transform?: string }) {
  return (
    <g transform={transform}>
      <rect x="42" y="45" width="145" height="17" rx="8.5" className="fill-spark-text-tertiary" />
      <text
        x="553"
        y="62"
        fontSize="26"
        fontWeight="500"
        textAnchor="end"
        className="fill-spark-text-secondary"
      >
        {t('components:document_graphics.invoice.title')}
      </text>
      <g fontSize="17" fontWeight="500" className="fill-spark-text-secondary">
        <text x="43" y="166">
          {t('components:document_graphics.invoice.bill_to')}
        </text>
        <text x="258" y="166">
          {t('components:document_graphics.invoice.ship_to')}
        </text>
        <text x="43" y="499">
          {t('components:document_graphics.invoice.tax_summary')}
        </text>
      </g>
      <g className="fill-spark-text-tertiary">
        {INVOICE_HEADINGS.map(([x, y, width]) => (
          <rect key={`${x}-${y}`} x={x} y={y} width={width} height="8.5" rx="4.25" />
        ))}
      </g>
      <g className="fill-spark-surface-strong">
        {INVOICE_LINES.map(([x, y, width]) => (
          <rect key={`${x}-${y}`} x={x} y={y} width={width} height="8" rx="4" />
        ))}
      </g>
    </g>
  );
}

export function InvoiceContentZonesGraphic() {
  return (
    <svg viewBox="0 0 1090 974" aria-hidden="true">
      <rect
        x="248"
        y="52"
        width="592"
        height="960"
        rx="10"
        className="fill-spark-surface stroke-spark-border-subtle drop-shadow-sm"
      />
      <InvoiceDocument transform="translate(248 52)" />
      {CONTENT_ZONES.map(({ top, stroke, text }, index) => (
        <g key={top}>
          <rect
            x="278.5"
            y={top}
            width="522.5"
            height="84.5"
            rx="10"
            strokeWidth="3"
            className={`fill-spark-surface ${stroke}`}
          />
          <rect
            x="293"
            y={top + 14.5}
            width="118"
            height="8.5"
            rx="4.25"
            className="fill-spark-text-tertiary"
          />
          <rect
            x="293"
            y={top + 34}
            width="229"
            height="8"
            rx="4"
            className="fill-spark-surface-strong"
          />
          <rect
            x="293"
            y={top + 53.5}
            width="172"
            height="8"
            rx="4"
            className="fill-spark-surface-strong"
          />
          <text
            x="777"
            y={top + 46.5}
            fontSize="20"
            fontWeight="600"
            textAnchor="end"
            className={text}
          >
            {t('components:document_graphics.content_zone', { number: index + 1 })}
          </text>
        </g>
      ))}
    </svg>
  );
}

export function PdfsGraphic() {
  return (
    <svg viewBox="0 0 692 628" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect
        x="70"
        y="71.5"
        width="553"
        height="600"
        rx="9"
        strokeWidth="3"
        className="fill-spark-surface stroke-spark-border-default"
      />
      <InvoiceDocument transform="translate(68.5 70) scale(0.94)" />
    </svg>
  );
}

export function EmailsGraphic() {
  return (
    <svg viewBox="0 0 692 628" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect
        x="69.5"
        y="82.5"
        width="553"
        height="600"
        rx="9"
        strokeWidth="3"
        className="fill-spark-surface stroke-spark-border-default"
      />
      <rect x="113" y="126" width="136" height="16" rx="8" className="fill-spark-text-tertiary" />
      {[155, 420].map((top) => (
        <g key={top} className="fill-spark-surface-strong">
          <rect x="113" y={top} width="406" height="11" rx="5.5" />
          <rect x="113" y={top + 17} width="376" height="11" rx="5.5" />
        </g>
      ))}
      {EMAIL_PANELS.map((top) => (
        <g key={top}>
          <rect
            x="113"
            y={top}
            width="474"
            height="133"
            rx="8"
            className="fill-spark-surface-strong"
          />
          <g className="fill-spark-border-default">
            <rect x="141" y={top + 31} width="414" height="10" rx="5" />
            <rect x="141" y={top + 51} width="414" height="10" rx="5" />
            <rect x="141" y={top + 71} width="305" height="10" rx="5" />
            <rect x="141" y={top + 92} width="248" height="10" rx="5" />
          </g>
        </g>
      ))}
      <rect x="113" y="354" width="139" height="45" rx="8" className="fill-illustration-primary" />
      <rect x="139" y="373" width="88" height="7" rx="3.5" className="fill-white" />
    </svg>
  );
}
